import { useEffect, useRef, useState } from 'react'
import {
  useForm,
  useFormContext,
  FormProvider,
  Controller,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Text, Group, Stack } from '@mantine/core'
import {
  Autocomplete,
  LoadScriptProps,
  useJsApiLoader,
} from '@react-google-maps/api'
import { MapPin } from 'lucide-react'
import toast from 'react-hot-toast'
import { all } from 'nigeria-states-lgas'

// Import our custom UI components
import { TextInput } from '@/components/ui/TextInput'
import { Select } from '@/components/ui/Select'
import { SchedulingToggle } from '@/components/ui/SchedulingToggle'
import { TimeInput } from '@/components/ui/TimeInput'
import { DateInput } from '@/components/ui/DateInput'
import { NumericInput } from '@/components/ui/NumericInput'
import { Checkbox } from '@/components/ui/Checkbox'
import { useDispatcher } from '@/providers/DispatcherProvider'

// Import validation schema and types
import {
  ambulanceRequestInformationSchema,
  defaultAmbulanceRequestInformationValues,
  AmbulanceRequestInformationFormValues,
} from '@/validation/schemas/ambulanceRequestInformation'
import {
  AmbulanceRequestInformationFormProps,
  CallerInformationFormValues,
} from '@/types/cases'
import {
  schedulingOptions,
  genderOptions,
  consciousnessOptions,
  oxygenRequiredOptions,
  severityLevelOptions,
  provisionalDiagnosisOptions,
} from './caseConstants'

// Google Maps configuration
const libraries: LoadScriptProps['libraries'] = ['places']
const googleMapApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
const nigeriaData = all()
const nigeriaStateOptions = nigeriaData.map(stateData => ({
  value: stateData.state,
  label: stateData.state,
}))
const stateAliases: Record<string, string> = {
  abuja: 'Federal Capital Territory',
  fct: 'Federal Capital Territory',
  nasarawa: 'Nassarawa',
}

const normalizeStateKey = (value: string) =>
  value.toLowerCase().replace(/[^a-z]/g, '')

const getCanonicalNigeriaState = (stateName?: string | null) => {
  if (!stateName) return ''

  const normalizedState = normalizeStateKey(stateName)
  const aliasedState = stateAliases[normalizedState]
  if (aliasedState) return aliasedState

  return (
    nigeriaData.find(
      stateData => normalizeStateKey(stateData.state) === normalizedState
    )?.state || ''
  )
}

const getStateFromPrecinctName = (precinctName?: string | null) => {
  if (!precinctName) return ''

  const stateName = precinctName.replace(/\s+dispatch\s+center.*$/i, '').trim()

  return getCanonicalNigeriaState(stateName)
}

const getStoredDispatcherPrecinctName = () => {
  if (typeof window === 'undefined') return ''

  try {
    const rawDispatcher = window.localStorage.getItem('dispatcher-data')
    if (!rawDispatcher) return ''

    const dispatcherData = JSON.parse(rawDispatcher) as {
      precinct_name?: unknown
    }

    return typeof dispatcherData.precinct_name === 'string'
      ? dispatcherData.precinct_name
      : ''
  } catch {
    return ''
  }
}

const getLgasForState = (stateName?: string) => {
  if (!stateName) return []

  const stateData = nigeriaData.find(
    stateData => stateData.state.toLowerCase() === stateName.toLowerCase()
  )

  return stateData?.lgas || []
}

const getLgaOptionsForState = (stateName?: string) =>
  getLgasForState(stateName).map(lga => ({
    value: lga,
    label: lga,
  }))

/**
 * AmbulanceRequestInformation Component
 *
 * A comprehensive form component for capturing ambulance request information.
 * Supports both standalone mode (manages its own form state) and formContext mode
 * (integrates with parent form via React Hook Form context).
 *
 * @param mode - 'standalone' for independent form, 'formContext' for parent form integration
 * @param initialValues - Default values for form fields
 * @param onSubmit - Callback when form is submitted (standalone mode)
 * @param onChange - Callback when form values change (live updates)
 * @param className - Additional CSS classes
 * @param disabled - Disable all form inputs
 */
export default function AmbulanceRequestInformation({
  mode = 'standalone',
  initialValues = {},
  onSubmit,
  onChange,
  className = '',
  disabled = false,
  callerInformation,
}: AmbulanceRequestInformationFormProps & {
  callerInformation?: Partial<CallerInformationFormValues> | null
}) {
  const { dispatcher } = useDispatcher()
  const dispatcherPrecinctState = getStateFromPrecinctName(
    dispatcher?.precinct_name || getStoredDispatcherPrecinctName()
  )
  const initialPickupState =
    getCanonicalNigeriaState(initialValues.pickup_state) ||
    dispatcherPrecinctState ||
    initialValues.pickup_state
  const initialDestinationState =
    getCanonicalNigeriaState(initialValues.destination_state) ||
    dispatcherPrecinctState ||
    initialValues.destination_state

  const parseDobToUtcDate = (dob: unknown): Date | null => {
    if (!dob) return null
    if (dob instanceof Date) return isNaN(dob.getTime()) ? null : dob

    if (typeof dob === 'string') {
      const trimmed = dob.trim()
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)
      if (match) {
        const year = Number(match[1])
        const month = Number(match[2])
        const day = Number(match[3])
        if (
          Number.isFinite(year) &&
          Number.isFinite(month) &&
          Number.isFinite(day)
        ) {
          const utc = new Date(Date.UTC(year, month - 1, day))
          return isNaN(utc.getTime()) ? null : utc
        }
      }

      const parsed = new Date(trimmed)
      return isNaN(parsed.getTime()) ? null : parsed
    }

    return null
  }

  const calculateAgeYears = (
    dob: unknown,
    now: Date = new Date()
  ): number | null => {
    const birthDate = parseDobToUtcDate(dob)
    if (!birthDate) return null

    const nowY = now.getUTCFullYear()
    const nowM = now.getUTCMonth()
    const nowD = now.getUTCDate()
    const birthY = birthDate.getUTCFullYear()
    const birthM = birthDate.getUTCMonth()
    const birthD = birthDate.getUTCDate()

    let age = nowY - birthY
    if (nowM < birthM || (nowM === birthM && nowD < birthD)) age -= 1
    if (!Number.isFinite(age)) return null

    // keep within schema bounds
    return Math.min(150, Math.max(0, age))
  }

  // Google Maps Autocomplete instances
  const [pickupAutocomplete, setPickupAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null)
  const [destinationAutocomplete, setDestinationAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null)

  // Refs for uncontrolled inputs - prevents React re-render interference with Google Autocomplete
  const pickupInputRef = useRef<HTMLInputElement>(null)
  const destinationInputRef = useRef<HTMLInputElement>(null)

  // Refs to track if place was just selected (to prevent onBlur from clearing coordinates)
  const pickupPlaceJustSelected = useRef(false)
  const destinationPlaceJustSelected = useRef(false)

  // Load Google Maps API
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: googleMapApiKey,
    libraries: libraries,
  })

  // Create form instance for standalone mode
  const standaloneForm = useForm<AmbulanceRequestInformationFormValues>({
    resolver: zodResolver(ambulanceRequestInformationSchema),
    defaultValues: {
      ...defaultAmbulanceRequestInformationValues,
      ...initialValues,
      pickup_state: initialPickupState,
      destination_state: initialDestinationState,
    },
    mode: 'onChange',
  })

  // Get form context for formContext mode
  const formContext = useFormContext<AmbulanceRequestInformationFormValues>()

  // Use appropriate form instance based on mode
  const form = mode === 'standalone' ? standaloneForm : formContext

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = form

  // Watch form values for live updates
  const watchedValues = watch()
  const schedulingType = watch('scheduling_type')
  const sameAsCaller = watch('same_as_caller')
  const watchedDob = watch('date_of_birth')
  const pickupState = watch('pickup_state')
  const destinationState = watch('destination_state')
  const pickupLgaOptions = getLgaOptionsForState(pickupState)
  const destinationLgaOptions = getLgaOptionsForState(destinationState)
  const previousValuesRef =
    useRef<AmbulanceRequestInformationFormValues | null>(null)
  const dispatcherDefaultedStateFieldsRef = useRef({
    pickup_state: !initialValues.pickup_state && Boolean(initialPickupState),
    destination_state:
      !initialValues.destination_state && Boolean(initialDestinationState),
  })

  useEffect(() => {
    const applyDispatcherStateDefault = (
      fieldName: 'pickup_state' | 'destination_state'
    ) => {
      const currentState = form.getValues(fieldName)
      const canonicalCurrentState = getCanonicalNigeriaState(currentState)

      if (canonicalCurrentState && canonicalCurrentState !== currentState) {
        form.setValue(fieldName, canonicalCurrentState, {
          shouldValidate: true,
        })
        return
      }

      const shouldApplyDispatcherDefault =
        !currentState || dispatcherDefaultedStateFieldsRef.current[fieldName]
      if (!shouldApplyDispatcherDefault || !dispatcherPrecinctState) return

      form.setValue(fieldName, dispatcherPrecinctState, {
        shouldValidate: true,
      })
      dispatcherDefaultedStateFieldsRef.current[fieldName] = true
    }

    applyDispatcherStateDefault('pickup_state')
    applyDispatcherStateDefault('destination_state')
  }, [dispatcherPrecinctState, form])

  // Handle form submission
  const handleFormSubmit = (values: AmbulanceRequestInformationFormValues) => {
    if (onSubmit) {
      onSubmit(values)
    }
  }

  // Handle live form changes - only when values actually change
  useEffect(() => {
    if (onChange && watchedValues) {
      // Check if values have actually changed by comparing with previous values
      const hasChanged =
        !previousValuesRef.current ||
        JSON.stringify(watchedValues) !==
          JSON.stringify(previousValuesRef.current)

      if (hasChanged) {
        previousValuesRef.current = watchedValues
        onChange(watchedValues)
      }
    }
  }, [watchedValues, onChange])

  // Handle "Same as caller" checkbox logic
  useEffect(() => {
    if (sameAsCaller && callerInformation) {
      // Populate patient fields with caller information
      const callerData = {
        first_name: callerInformation.first_name || '',
        last_name: callerInformation.last_name || '',
        date_of_birth: callerInformation.date_of_birth || new Date(),
        gender: callerInformation.gender || 'male',
      }

      // Update form values
      form.setValue('first_name', callerData.first_name)
      form.setValue('last_name', callerData.last_name)
      form.setValue('date_of_birth', callerData.date_of_birth)
      form.setValue('gender', callerData.gender)
    } else if (!sameAsCaller) {
      // Clear patient fields when unchecked (optional - user can manually clear)
      // We don't automatically clear to avoid losing user input
    }
  }, [sameAsCaller, callerInformation, form])

  // If patient is same as caller, keep Age derived from Date of Birth (UI shows computed age instead of 0).
  useEffect(() => {
    if (!sameAsCaller) return
    const computedAge = calculateAgeYears(watchedDob)
    if (typeof computedAge !== 'number') return

    const currentAge = form.getValues('age')
    if (currentAge === computedAge) return

    form.setValue('age', computedAge, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
  }, [sameAsCaller, watchedDob, form])

  // Auto-fill pickup location from caller information on mount
  const hasAutoFilledPickup = useRef(false)
  useEffect(() => {
    if (
      callerInformation?.location &&
      !hasAutoFilledPickup.current &&
      !initialValues?.pickup_location
    ) {
      hasAutoFilledPickup.current = true
      // Update form value
      form.setValue('pickup_location', callerInformation.location, {
        shouldValidate: true,
        shouldDirty: true,
      })
      // Also update the DOM input directly for uncontrolled input
      if (pickupInputRef.current) {
        pickupInputRef.current.value = callerInformation.location
      }
      if (callerInformation.location_latitude) {
        form.setValue(
          'pickup_location_latitude',
          callerInformation.location_latitude
        )
      }
      if (callerInformation.location_longitude) {
        form.setValue(
          'pickup_location_longitude',
          callerInformation.location_longitude
        )
      }
    }
  }, [callerInformation, form, initialValues?.pickup_location])

  // Google Maps Autocomplete handlers
  const onPickupLoad = (
    autocompleteInstance: google.maps.places.Autocomplete
  ) => {
    setPickupAutocomplete(autocompleteInstance)
  }

  const onDestinationLoad = (
    autocompleteInstance: google.maps.places.Autocomplete
  ) => {
    setDestinationAutocomplete(autocompleteInstance)
  }

  const onPickupPlaceChanged = () => {
    if (pickupAutocomplete) {
      try {
        const place = pickupAutocomplete.getPlace()

        if (!place.geometry || !place.formatted_address) {
          throw new Error('Invalid place data received.')
        }

        // Mark that a place was just selected (prevents onBlur from clearing coordinates)
        pickupPlaceJustSelected.current = true

        // Extract location data
        const lat = place.geometry?.location?.lat()
        const lng = place.geometry?.location?.lng()
        const formattedAddress = place.formatted_address

        // Update the input value directly for uncontrolled input
        if (pickupInputRef.current) {
          pickupInputRef.current.value = formattedAddress
        }

        // Update form with location data
        form.setValue('pickup_location', formattedAddress, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        })
        if (lat && lng) {
          form.setValue('pickup_location_latitude', lat, {
            shouldValidate: true,
            shouldDirty: true,
          })
          form.setValue('pickup_location_longitude', lng, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
      } catch (error: any) {
        if (error?.message?.includes('CORS')) {
          toast.error(
            'There was an issue resolving the address. Please try again.'
          )
        } else {
          toast.error('Please select pickup location again.')
        }
      }
    } else {
      toast.error('Autocomplete is not loaded yet.')
    }
  }

  const onDestinationPlaceChanged = () => {
    if (destinationAutocomplete) {
      try {
        const place = destinationAutocomplete.getPlace()

        if (!place.geometry || !place.formatted_address) {
          throw new Error('Invalid place data received.')
        }

        // Mark that a place was just selected (prevents onBlur from clearing coordinates)
        destinationPlaceJustSelected.current = true

        // Extract location data
        const lat = place.geometry?.location?.lat()
        const lng = place.geometry?.location?.lng()
        const formattedAddress = place.formatted_address

        // Update the input value directly for uncontrolled input
        if (destinationInputRef.current) {
          destinationInputRef.current.value = formattedAddress
        }

        // Update form with location data
        form.setValue('destination', formattedAddress, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        })
        if (lat && lng) {
          form.setValue('dropoff_location_latitude', lat, {
            shouldValidate: true,
            shouldDirty: true,
          })
          form.setValue('dropoff_location_longitude', lng, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
      } catch (error: any) {
        console.error('Error in onDestinationPlaceChanged:', error)
        if (error?.message?.includes('CORS')) {
          toast.error(
            'There was an issue resolving the address. Please try again.'
          )
        } else {
          toast.error('Please select destination again.')
        }
      }
    } else {
      toast.error('Autocomplete is not loaded yet.')
    }
  }

  // Render form content
  const renderFormContent = () => (
    <div
      className={`space-y-6 border border-light-gray-20 p-4 gap-4 rounded-md w-full min-w-2xl min-h-0 overflow-y-auto ${className}`}
    >
      {/* Location Information Section */}
      <div className="">
        <div className="flex items-center  mb-md border-b border-light-gray-20 pb-1 mb-4">
          <Text size="md" fw={600} c="grey.4">
            Ambulance Request Information - Interstate Transfer
          </Text>
        </div>
      </div>

      {/* Location Information Section */}
      <div className="">
        <Group mb="md">
          <Text size="md" fw={600} c="primary.6">
            Location Information
          </Text>
        </Group>

        <Stack gap="md">
          {/* Pickup Location Row */}
          <Group grow>
            <Controller
              name="pickup_location"
              control={control}
              render={({ field }) => (
                <div className="flex-1">
                  <Text size="sm" fw={500} mb={4}>
                    Pickup Location <span className="text-red-500">*</span>
                  </Text>
                  {isLoaded ? (
                    <Autocomplete
                      onLoad={onPickupLoad}
                      onPlaceChanged={onPickupPlaceChanged}
                      options={{
                        componentRestrictions: { country: 'ng' },
                        fields: [
                          'formatted_address',
                          'geometry',
                          'address_components',
                        ],
                      }}
                    >
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 z-10 pointer-events-none" />
                        <input
                          ref={pickupInputRef}
                          type="text"
                          placeholder="Enter pickup location"
                          defaultValue={field.value || ''}
                          onBlur={e => {
                            // If a place was just selected, don't clear coordinates
                            if (pickupPlaceJustSelected.current) {
                              pickupPlaceJustSelected.current = false
                              return
                            }
                            // Sync typed value to form on blur
                            const currentValue = e.target.value
                            if (currentValue !== field.value) {
                              field.onChange(currentValue)
                              // Clear coordinates since user typed manually
                              form.setValue(
                                'pickup_location_latitude',
                                undefined
                              )
                              form.setValue(
                                'pickup_location_longitude',
                                undefined
                              )
                            }
                          }}
                          disabled={disabled}
                          className="w-full pl-10 pr-4 py-2 rounded-md"
                          style={{
                            backgroundColor: '#F5F5F7',
                            border: errors.pickup_location
                              ? '1px solid #fa5252'
                              : 'none',
                            paddingLeft: '2.5rem',
                            fontSize: '0.875rem',
                            color: disabled ? '#9CA3AF' : '#111827',
                            cursor: disabled ? 'not-allowed' : 'text',
                          }}
                        />
                      </div>
                    </Autocomplete>
                  ) : (
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 z-10 pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Loading..."
                        disabled
                        className="w-full pl-10 pr-4 py-2 rounded-md"
                        style={{
                          backgroundColor: '#F5F5F7',
                          border: 'none',
                          paddingLeft: '2.5rem',
                          fontSize: '0.875rem',
                          color: '#9CA3AF',
                        }}
                      />
                    </div>
                  )}
                  {errors.pickup_location && (
                    <Text size="xs" c="red" mt={4}>
                      {errors.pickup_location.message}
                    </Text>
                  )}
                </div>
              )}
            />

            <Controller
              name="pickup_state"
              control={control}
              render={({ field }) => (
                <Select
                  label="State"
                  placeholder="Select state"
                  required
                  data={nigeriaStateOptions}
                  value={field.value}
                  onChange={value => {
                    const nextState = value || ''
                    dispatcherDefaultedStateFieldsRef.current.pickup_state = false
                    field.onChange(nextState)

                    if (nextState !== field.value) {
                      form.setValue('pickup_lga', '', {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      })
                    }
                  }}
                  error={errors.pickup_state?.message}
                  disabled={disabled}
                  searchable
                />
              )}
            />

            <Controller
              name="pickup_lga"
              control={control}
              render={({ field }) => (
                <Select
                  label="LGA"
                  placeholder={
                    pickupState ? 'Select LGA' : 'Select state first'
                  }
                  required
                  data={pickupLgaOptions}
                  value={field.value}
                  onChange={value => field.onChange(value || '')}
                  error={errors.pickup_lga?.message}
                  disabled={disabled || !pickupState}
                  searchable
                />
              )}
            />
          </Group>

          {/* Destination Row */}
          <Group grow>
            <Controller
              name="destination"
              control={control}
              render={({ field }) => (
                <div className="flex-1">
                  <Text size="sm" fw={500} mb={4}>
                    Destination <span className="text-red-500">*</span>
                  </Text>
                  {isLoaded ? (
                    <Autocomplete
                      onLoad={onDestinationLoad}
                      onPlaceChanged={onDestinationPlaceChanged}
                      options={{
                        componentRestrictions: { country: 'ng' },
                        fields: [
                          'formatted_address',
                          'geometry',
                          'address_components',
                        ],
                      }}
                    >
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 z-10 pointer-events-none" />
                        <input
                          ref={destinationInputRef}
                          type="text"
                          placeholder="Enter destination"
                          defaultValue={field.value || ''}
                          onBlur={e => {
                            // If a place was just selected, don't clear coordinates
                            if (destinationPlaceJustSelected.current) {
                              destinationPlaceJustSelected.current = false
                              return
                            }
                            // Sync typed value to form on blur
                            const currentValue = e.target.value
                            if (currentValue !== field.value) {
                              field.onChange(currentValue)
                              // Clear coordinates since user typed manually
                              form.setValue(
                                'dropoff_location_latitude',
                                undefined
                              )
                              form.setValue(
                                'dropoff_location_longitude',
                                undefined
                              )
                            }
                          }}
                          disabled={disabled}
                          className="w-full pl-10 pr-4 py-2 rounded-md"
                          style={{
                            backgroundColor: '#F5F5F7',
                            border: errors.destination
                              ? '1px solid #fa5252'
                              : 'none',
                            paddingLeft: '2.5rem',
                            fontSize: '0.875rem',
                            color: disabled ? '#9CA3AF' : '#111827',
                            cursor: disabled ? 'not-allowed' : 'text',
                          }}
                        />
                      </div>
                    </Autocomplete>
                  ) : (
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 z-10 pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Loading..."
                        disabled
                        className="w-full pl-10 pr-4 py-2 rounded-md"
                        style={{
                          backgroundColor: '#F5F5F7',
                          border: 'none',
                          paddingLeft: '2.5rem',
                          fontSize: '0.875rem',
                          color: '#9CA3AF',
                        }}
                      />
                    </div>
                  )}
                  {errors.destination && (
                    <Text size="xs" c="red" mt={4}>
                      {errors.destination.message}
                    </Text>
                  )}
                </div>
              )}
            />

            <Controller
              name="destination_state"
              control={control}
              render={({ field }) => (
                <Select
                  label="State"
                  placeholder="Select state"
                  required
                  data={nigeriaStateOptions}
                  value={field.value}
                  onChange={value => {
                    const nextState = value || ''
                    dispatcherDefaultedStateFieldsRef.current.destination_state = false
                    field.onChange(nextState)

                    if (nextState !== field.value) {
                      form.setValue('destination_lga', '', {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      })
                    }
                  }}
                  error={errors.destination_state?.message}
                  disabled={disabled}
                  searchable
                />
              )}
            />

            <Controller
              name="destination_lga"
              control={control}
              render={({ field }) => (
                <Select
                  label="LGA"
                  placeholder={
                    destinationState ? 'Select LGA' : 'Select state first'
                  }
                  required
                  data={destinationLgaOptions}
                  value={field.value}
                  onChange={value => field.onChange(value || '')}
                  error={errors.destination_lga?.message}
                  disabled={disabled || !destinationState}
                  searchable
                />
              )}
            />
          </Group>

          {/* Scheduling Controls (Horizontal Row) */}
          <div className="flex items-end gap-4">
            <Controller
              name="scheduling_type"
              control={control}
              defaultValue="now"
              render={({ field }: { field: any }) => (
                <SchedulingToggle
                  label=""
                  options={schedulingOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.scheduling_type?.message}
                  disabled={disabled}
                  className="shrink-0"
                />
              )}
            />

            <div
              className={`flex-1 transition-opacity duration-200 ${schedulingType === 'schedule' ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}
            >
              <div className="flex gap-2">
                <Controller
                  name="from_date"
                  control={control}
                  render={({ field }) => (
                    <DateInput
                      label="From"
                      placeholder="From"
                      value={field.value ? new Date(field.value) : null}
                      onChange={date =>
                        field.onChange(date?.toISOString().split('T')[0])
                      }
                      error={errors.from_date?.message}
                      disabled={disabled || schedulingType !== 'schedule'}
                    />
                  )}
                />

                <Controller
                  name="from_time"
                  control={control}
                  render={({ field }) => (
                    <TimeInput
                      label="."
                      format="HH:MM"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.from_time?.message}
                      disabled={disabled || schedulingType !== 'schedule'}
                    />
                  )}
                />

                <Controller
                  name="to_date"
                  control={control}
                  render={({ field }) => (
                    <DateInput
                      label="To"
                      placeholder="To"
                      value={field.value ? new Date(field.value) : null}
                      onChange={date =>
                        field.onChange(date?.toISOString().split('T')[0])
                      }
                      error={errors.to_date?.message}
                      disabled={disabled || schedulingType !== 'schedule'}
                    />
                  )}
                />

                <Controller
                  name="to_time"
                  control={control}
                  render={({ field }) => (
                    <TimeInput
                      label="."
                      format="HH:MM"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.to_time?.message}
                      disabled={disabled || schedulingType !== 'schedule'}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </Stack>
      </div>

      {/* Patients Information Section */}
      <div className="bg-white">
        <Group mb="md">
          <Text size="md" fw={600} c="primary.6">
            Patients Information
          </Text>
        </Group>

        <Stack gap="md">
          {/* Same as caller checkbox */}
          <Controller
            name="same_as_caller"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                <Checkbox
                  label="Same as caller's information"
                  checked={field.value}
                  onChange={field.onChange}
                  error={errors.same_as_caller?.message}
                  disabled={disabled}
                />
                {field.value && callerInformation && (
                  <div className="text-sm text-green-600 bg-green-50 p-2 rounded border border-green-200">
                    ✓ Patient information will be automatically filled from
                    caller details
                  </div>
                )}
              </div>
            )}
          />

          {/* Patient Details Row */}
          <Group grow>
            <TextInput
              label="First Name"
              placeholder="Enter first name"
              required
              register={register('first_name')}
              error={errors.first_name?.message}
              disabled={disabled}
              // className={sameAsCaller ? 'bg-green-50 border-green-200' : ''}
            />

            <TextInput
              label="Last Name"
              placeholder="Enter last name"
              required
              register={register('last_name')}
              error={errors.last_name?.message}
              disabled={disabled}
              // className={sameAsCaller ? 'bg-green-50 border-green-200' : ''}
            />
          </Group>

          {/* Date of Birth, Age, Weight, Gender - Single Horizontal Row */}
          <Group grow>
            <Controller
              name="date_of_birth"
              control={control}
              render={({ field }) => (
                <DateInput
                  label="Date of Birth"
                  placeholder="Select date of birth"
                  required
                  value={field.value ? new Date(field.value) : null}
                  onChange={date =>
                    field.onChange(date?.toISOString().split('T')[0])
                  }
                  error={errors.date_of_birth?.message}
                  disabled={disabled}
                  maxDate={new Date()}
                  // className={sameAsCaller ? 'bg-green-50 border-green-200' : ''}
                />
              )}
            />

            <Controller
              name="age"
              control={control}
              render={({ field }) => (
                <NumericInput
                  label="Age"
                  placeholder="Enter age"
                  required
                  min={0}
                  max={150}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.age?.message}
                  rightSection={
                    <span className="text-gray-400 text-sm">yrs</span>
                  }
                  disabled={disabled}
                />
              )}
            />

            <Controller
              name="weight"
              control={control}
              render={({ field }) => (
                <NumericInput
                  label="Weight"
                  placeholder="Enter weight"
                  min={0}
                  max={500}
                  step={0.1}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.weight?.message}
                  rightSection={
                    <span className="text-gray-400 text-sm">Kg</span>
                  }
                  disabled={disabled}
                />
              )}
            />

            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <Select
                  label="Gender"
                  placeholder="Select gender"
                  required
                  data={genderOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.gender?.message}
                  disabled={disabled}
                  // className={sameAsCaller ? 'bg-green-50 border-green-200' : ''}
                />
              )}
            />
          </Group>
        </Stack>
      </div>

      {/* Medical Information Section */}
      <div className="">
        <Group mb="md">
          <Text size="md" fw={600} c="primary.6">
            Medical Information
          </Text>
        </Group>

        <Stack gap="md">
          {/* Consciousness and Oxygen Row */}
          <Group grow>
            <Controller
              name="is_conscious"
              control={control}
              render={({ field }) => (
                <Select
                  label="Is patient conscious?"
                  placeholder="Select consciousness level"
                  required
                  data={consciousnessOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.is_conscious?.message}
                  disabled={disabled}
                />
              )}
            />

            <Controller
              name="oxygen_required"
              control={control}
              render={({ field }) => (
                <Select
                  label="Is oxygen required?"
                  placeholder="Select oxygen requirement"
                  required
                  data={oxygenRequiredOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.oxygen_required?.message}
                  disabled={disabled}
                />
              )}
            />
          </Group>

          {/* Diagnosis and Severity Row */}
          <Group grow>
            <Controller
              name="provisional_diagnosis"
              control={control}
              render={({ field }) => (
                <Select
                  label="Provisional Diagnosis"
                  placeholder="Select diagnosis"
                  required
                  data={provisionalDiagnosisOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.provisional_diagnosis?.message}
                  disabled={disabled}
                />
              )}
            />

            <Controller
              name="severity_level"
              control={control}
              render={({ field }) => (
                <Select
                  label="Severity Level"
                  placeholder="Select severity"
                  required
                  data={severityLevelOptions}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.severity_level?.message}
                  disabled={disabled}
                />
              )}
            />
          </Group>
        </Stack>
      </div>
    </div>
  )

  // Render based on mode
  if (mode === 'standalone') {
    return (
      <FormProvider {...standaloneForm}>
        <form
          id="ambulance-request-information-form"
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          {renderFormContent()}
        </form>
      </FormProvider>
    )
  }

  // FormContext mode - just render the content
  return renderFormContent()
}

// Export the form values type for external use
export type { AmbulanceRequestInformationFormValues } from '@/validation/schemas/ambulanceRequestInformation'
